// Organization Hierarchy report (ADR-096, presentation E1 — expandable rows).
//
// One skeleton call renders the whole cluster → BU → Project → Team structure with
// owners/managers and per-project headcounts; employees (allocations) are
// fetched per project when its row is expanded, cached for the session.
// Replaces the D3 proof of concept, which eager-loaded the entire company and
// every employee photo up front.
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { TextField, CircularProgress, Tooltip, Alert } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import 'dayjs/locale/en-gb';
import { Container, Header, HeaderLeft, Title, Content, MainSection, BackButton } from '../styles';
import {
  FiltersBar,
  HeaderStats,
  HeaderStat,
  TreeRow,
  ExpandButton,
  ExpandSpacer,
  TypeTag,
  MutedTag,
  NodeName,
  NodeDetail,
  LifecycleTag,
  RowAside,
  PersonCell,
  CountTag,
  StatusChip,
  EngagementChip,
  AllocationInfo,
  InlineNote,
} from './styles';
import LoadingSpinner from '../../common/LoadingSpinner';
import EmptyState from '../../common/EmptyState';
import ClusterSelector from '../../common/ClusterSelector';
import BusinessUnitSelector from '../../common/BusinessUnitSelector';
import OrgUnitTypeSelector from '../../common/OrgUnitTypeSelector';
import ProjectPurposeSelector from '../../common/ProjectPurposeSelector';
import EmployeeSearch from '../../common/EmployeeSearch';
import { EmployeeSearchProvider } from '../../common/EmployeeSearch/contexts/EmployeeSearchProvider';
import { EmployeeAvatar } from '../../common/EmployeeAvatar';
import { ORG_UNIT_TYPE_RETURN_TYPES } from '../../../constants/selectorReturnTypes';
import { organizationApi } from '../../../services/api';
import { useAlert } from '../../../contexts/AlertContext';
import { formatDateOnly, toPickerValue } from '../../../utils/dateOnly';

// POC legend colors, kept so the levels stay recognisable.
const TYPE_COLORS = {
  cluster: '#ff7f0e',
  businessUnit: '#1f77b4',
  project: '#9467bd',
  team: '#8c564b',
};

const TYPE_LABELS = {
  cluster: 'Cluster',
  businessUnit: 'Business Unit',
  project: 'Project',
  team: 'Team',
};

// ADR-104: the Unallocated node. Rendered AFTER the last cluster, never inside the
// forest — so it cannot reach countStructure, and no consumer of the skeleton
// can mistake it for a Cluster.
const UNALLOCATED_KEY = 'unallocated:root';

// Every cluster id in a cluster subtree. The cluster dropdown selects a SUBTREE, while
// Employee.clusterId is one exact id, so narrowing the node by cluster has to match
// against the whole subtree or it silently loses people in child clusters.
const collectClusterIds = (cluster, into = new Set()) => {
  into.add(cluster.id);
  cluster.childClusters.forEach((child) => collectClusterIds(child, into));
  return into;
};

// ---------------------------------------------------------------------------
// Pure filtering over the skeleton (all client-side: the skeleton is small).
// A node is kept when it matches, or when anything below it matches; a node
// that itself matches the search keeps its whole subtree.
// ---------------------------------------------------------------------------

const matchesTerm = (term, ...values) =>
  values.some((value) => (value || '').toLowerCase().includes(term));

const teamTreeMatches = (team, term) =>
  matchesTerm(term, team.name, team.manager?.fullName)
  || team.childTeams.some((child) => teamTreeMatches(child, term));

const projectMatches = (project, term) =>
  matchesTerm(term, project.name, project.code, project.manager?.fullName)
  || project.teams.some((team) => teamTreeMatches(team, term));

const filterBusinessUnit = (bu, orgUnitTypeCode, term, purposeId) => {
  const typeOk = !orgUnitTypeCode || bu.orgUnitType?.code === orgUnitTypeCode;
  const selfMatch = !term || matchesTerm(term, bu.name, bu.owner?.fullName);

  const childBus = bu.childBus
    .map((child) => filterBusinessUnit(child, orgUnitTypeCode, term, purposeId))
    .filter(Boolean);

  let projects = [];
  if (typeOk) {
    projects = (!term || selfMatch)
      ? bu.projects
      : bu.projects.filter((project) => projectMatches(project, term));
    // The purpose dropdown is a hard project restriction — it applies even
    // inside BUs that match the search.
    if (purposeId) {
      projects = projects.filter((project) => project.purpose?.id === purposeId);
    }
  }

  // With a purpose selected, only paths leading to matching projects survive.
  const keep = childBus.length > 0
    || (typeOk && (purposeId ? projects.length > 0 : (selfMatch || projects.length > 0)));
  return keep ? { ...bu, childBus, projects } : null;
};

const filterCluster = (cluster, orgUnitTypeCode, term, purposeId) => {
  const selfMatch = !term
    || matchesTerm(term, cluster.name, ...cluster.managers.map((manager) => manager.fullName));

  const childClusters = cluster.childClusters
    .map((child) => filterCluster(child, orgUnitTypeCode, term, purposeId))
    .filter(Boolean);

  // A cluster that matches the search shows its full (still type-filtered)
  // contents rather than an empty shell.
  const buTerm = term && selfMatch ? '' : term;
  const businessUnits = cluster.businessUnits
    .map((bu) => filterBusinessUnit(bu, orgUnitTypeCode, buTerm, purposeId))
    .filter(Boolean);

  const keep = childClusters.length > 0
    || businessUnits.length > 0
    || (purposeId ? false : (term ? selfMatch : !orgUnitTypeCode));
  return keep ? { ...cluster, childClusters, businessUnits } : null;
};

const findClusterSubtree = (clusters, clusterId) => {
  for (const cluster of clusters) {
    if (cluster.id === clusterId) return cluster;
    const found = findClusterSubtree(cluster.childClusters, clusterId);
    if (found) return found;
  }
  return null;
};

const findBuSubtree = (businessUnits, businessUnitId) => {
  for (const bu of businessUnits) {
    if (bu.id === businessUnitId) return bu;
    const found = findBuSubtree(bu.childBus, businessUnitId);
    if (found) return found;
  }
  return null;
};

// Prune the forest down to paths that lead to the given projects — the
// Individual filter's hard restriction (only where that person is allocated).
const restrictToProjects = (roots, projectIds) => {
  const restrictBu = (bu) => {
    const childBus = bu.childBus.map(restrictBu).filter(Boolean);
    const projects = bu.projects.filter((project) => projectIds.has(project.id));
    if (childBus.length === 0 && projects.length === 0) return null;
    return { ...bu, childBus, projects };
  };
  const restrictCluster = (cluster) => {
    const childClusters = cluster.childClusters.map(restrictCluster).filter(Boolean);
    const businessUnits = cluster.businessUnits.map(restrictBu).filter(Boolean);
    if (childClusters.length === 0 && businessUnits.length === 0) return null;
    return { ...cluster, childClusters, businessUnits };
  };
  return roots.map(restrictCluster).filter(Boolean);
};

/** Narrow to the selected cluster / BU subtrees, then apply type + purpose + search filters. */
const applyFilters = (roots, { clusterId, businessUnitId, orgUnitTypeCode, term, individualProjectIds, projectPurposeId }) => {
  let forest = roots;

  if (individualProjectIds) {
    forest = restrictToProjects(forest, individualProjectIds);
  }

  if (clusterId) {
    const subtree = findClusterSubtree(forest, clusterId);
    forest = subtree ? [subtree] : [];
  }

  if (businessUnitId) {
    forest = forest
      .map((cluster) => {
        const walk = (node) => {
          const inOwn = findBuSubtree(node.businessUnits, businessUnitId);
          const childClusters = node.childClusters.map(walk).filter(Boolean);
          if (!inOwn && childClusters.length === 0) return null;
          return {
            ...node,
            businessUnits: inOwn ? [inOwn] : [],
            childClusters,
          };
        };
        return walk(cluster);
      })
      .filter(Boolean);
  }

  return forest
    .map((cluster) => filterCluster(cluster, orgUnitTypeCode, term, projectPurposeId))
    .filter(Boolean);
};

const countStructure = (roots) => {
  const totals = { clusters: 0, businessUnits: 0, projects: 0 };
  const visitBu = (bu) => {
    totals.businessUnits += 1;
    totals.projects += bu.projects.length;
    bu.childBus.forEach(visitBu);
  };
  const visitCluster = (cluster) => {
    totals.clusters += 1;
    cluster.businessUnits.forEach(visitBu);
    cluster.childClusters.forEach(visitCluster);
  };
  roots.forEach(visitCluster);
  return totals;
};

const collectTeamIds = (teams, into = new Set()) => {
  teams.forEach((team) => {
    into.add(team.id);
    collectTeamIds(team.childTeams, into);
  });
  return into;
};

const OrganizationHierarchy = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();

  const [skeleton, setSkeleton] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  // Filters
  const [clusterId, setClusterId] = useState('');
  const [businessUnitId, setBusinessUnitId] = useState('');
  const [orgUnitTypeCode, setOrgUnitTypeCode] = useState('');
  const [projectPurposeId, setProjectPurposeId] = useState('');
  const [search, setSearch] = useState('');
  // Individual filter (ADR-096 Amendment 3): the selected employee, and their
  // active allocation refs fetched from the API —
  // { status: 'loading' | 'loaded' | 'error', projectIds?: Set }
  const [individual, setIndividual] = useState(null);
  const [individualRefs, setIndividualRefs] = useState(null);

  // Expansion state (node keys) and the per-project allocation cache:
  // { [projectId]: { status: 'loading' | 'loaded' | 'error', rows? } }
  const [expanded, setExpanded] = useState(() => new Set());
  const [allocationsByProject, setAllocationsByProject] = useState({});

  // ADR-104: counts on mount (fixed size), rows on expand — the same split the
  // skeleton/allocations pair uses, so mount cost never scales with headcount.
  // { total, byCluster: [{ clusterId, clusterName, count }] }
  const [unallocated, setUnallocated] = useState(null);
  // { status: 'loading' | 'loaded' | 'error', rows? }
  const [unallocatedRows, setUnallocatedRows] = useState(null);

  // ADR-103: as-of mode — the structure as it was on a past date. The cache
  // and expansion reset on date change; historyStartsAt (from the envelope)
  // clamps the picker; preHistory marks a refused pre-backfill date.
  const [asOfDate, setAsOfDate] = useState(null);
  const [historyStartsAt, setHistoryStartsAt] = useState(null);
  const [preHistory, setPreHistory] = useState(false);
  const asOfKey = asOfDate && asOfDate.isValid() ? asOfDate.format('YYYY-MM-DD') : null;
  const asOfActive = Boolean(asOfKey);

  // ADR-103: learn the history floor up front so the picker is clamped from
  // the first open, not only after the first as-of response echoes it back.
  useEffect(() => {
    let cancelled = false;
    organizationApi.getHierarchyHistoryStart()
      .then((data) => {
        if (!cancelled && data?.historyStartsAt) {
          setHistoryStartsAt(data.historyStartsAt);
        }
      })
      .catch(() => {
        // Non-fatal: the picker just stays unclamped until an as-of response
        // (or the pre-history empty state) supplies the bound.
      });
    return () => { cancelled = true; };
  }, []);

  // ADR-104: the Unallocated node's count, in parallel with the skeleton.
  // Amendment 1: available in as-of mode too — employment at T from employment
  // episodes (hire/resign dates as fallback), allocation at T from the ADR-103
  // employee_projects ∪ history union. Counts for one date are wrong for
  // another, so the rows cache resets with the date.
  useEffect(() => {
    let cancelled = false;
    setUnallocated(null);
    setUnallocatedRows(null);
    organizationApi.getUnallocatedSummary(asOfKey)
      .then((data) => { if (!cancelled) setUnallocated(data); })
      .catch((error) => {
        if (cancelled) return;
        showAlert('Failed to load the unallocated count.', 'error', error);
        // Keep the node: a missing node is indistinguishable from "nobody is
        // unallocated", which is the one thing it must never imply. It renders
        // with an unknown count and still expands.
        setUnallocated({ total: null, byCluster: [] });
      });
    return () => { cancelled = true; };
  }, [asOfKey, showAlert]);

  useEffect(() => {
    const fetchSkeleton = async () => {
      try {
        setIsLoading(true);
        setPreHistory(false);
        // ADR-103: allocations fetched for one date are wrong for another —
        // the cache and the tree reset together with the mode.
        setAllocationsByProject({});
        // ADR-120: units ended before the reference date are pruned server-side
        const data = await organizationApi.getHierarchy(asOfKey);
        // As-of responses are enveloped; the live response is a bare array.
        const forest = asOfKey ? data.hierarchy : data;
        if (asOfKey && data.historyStartsAt) {
          setHistoryStartsAt(data.historyStartsAt);
        }
        setSkeleton(forest);
        setLoadFailed(false);
        // Root clusters start open so the page is a structure, not a single line.
        setExpanded(new Set(forest.map((cluster) => `cluster:${cluster.id}`)));
      } catch (error) {
        // ADR-103: a refused pre-backfill date is a state, not a failure —
        // history simply does not reach that far (and is never approximated).
        if (asOfKey && error.status === 400) {
          setPreHistory(true);
          setSkeleton(null);
          if (error.data?.historyStartsAt) {
            setHistoryStartsAt(error.data.historyStartsAt);
          }
        } else {
          setLoadFailed(true);
          showAlert('Failed to load the organization hierarchy.', 'error', error);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchSkeleton();
  }, [showAlert, asOfKey]);

  // Resolve where the selected individual sits: fetch their allocation refs.
  useEffect(() => {
    if (!individual) {
      setIndividualRefs(null);
      return;
    }
    let cancelled = false;
    const fetchRefs = async () => {
      setIndividualRefs({ status: 'loading' });
      try {
        const data = await organizationApi.getIndividualAllocations(individual.id);
        if (cancelled) return;
        setIndividualRefs({
          status: 'loaded',
          projectIds: new Set(data.allocations.map((allocation) => allocation.projectId)),
          // ADR-104: server-side answer, not inferred from an empty array — an
          // empty array is also what a resigned individual looks like.
          unallocated: Boolean(data.unallocated),
        });
      } catch (error) {
        if (cancelled) return;
        showAlert("Failed to load the individual's allocations.", 'error', error);
        setIndividualRefs({ status: 'error', projectIds: new Set() });
      }
    };
    fetchRefs();
    return () => { cancelled = true; };
  }, [individual, showAlert]);

  const loadAllocations = useCallback(async (projectId) => {
    setAllocationsByProject((prev) => ({ ...prev, [projectId]: { status: 'loading' } }));
    try {
      // ADR-103: in as-of mode the rows are those active AT the date
      const data = await organizationApi.getProjectAllocations(projectId, asOfKey);
      setAllocationsByProject((prev) => ({
        ...prev,
        [projectId]: { status: 'loaded', rows: data.allocations },
      }));
    } catch (error) {
      showAlert('Failed to load project allocations.', 'error', error);
      setAllocationsByProject((prev) => ({ ...prev, [projectId]: { status: 'error' } }));
    }
  }, [showAlert, asOfKey]);

  const loadUnallocated = useCallback(async () => {
    setUnallocatedRows({ status: 'loading' });
    try {
      const data = await organizationApi.getUnallocatedIndividuals(asOfKey);
      // cluster groups arrive COLLAPSED. Expanding a node in this report reveals
      // its immediate children and nothing below them — the same contract as
      // cluster → BU, BU → project, project → sub-team. The node is not an
      // exception to it.
      setUnallocatedRows({ status: 'loaded', rows: data.individuals });
    } catch (error) {
      showAlert('Failed to load unallocated individuals.', 'error', error);
      setUnallocatedRows({ status: 'error' });
    }
  }, [showAlert, asOfKey]);

  const toggleNode = useCallback((key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const toggleProject = useCallback((project) => {
    const key = `project:${project.id}`;
    if (!expanded.has(key) && !allocationsByProject[project.id]) {
      loadAllocations(project.id);
    }
    toggleNode(key);
  }, [expanded, allocationsByProject, loadAllocations, toggleNode]);

  const toggleUnallocated = useCallback(() => {
    if (!expanded.has(UNALLOCATED_KEY) && !unallocatedRows) {
      loadUnallocated();
    }
    toggleNode(UNALLOCATED_KEY);
  }, [expanded, unallocatedRows, loadUnallocated, toggleNode]);

  // ADR-104: the Individual filter's old dead end. A person with no active
  // allocation used to produce an empty report naming them; now they resolve
  // into the node, which therefore has to have its rows.
  useEffect(() => {
    if (individualRefs?.status === 'loaded' && individualRefs.unallocated && !unallocatedRows) {
      loadUnallocated();
    }
  }, [individualRefs, unallocatedRows, loadUnallocated]);

  // The individual's projects auto-expand, which means their allocations must
  // be in the cache — fetch whichever are missing once the refs arrive.
  useEffect(() => {
    if (individualRefs?.status !== 'loaded') return;
    individualRefs.projectIds.forEach((projectId) => {
      if (!allocationsByProject[projectId]) {
        loadAllocations(projectId);
      }
    });
  }, [individualRefs, allocationsByProject, loadAllocations]);

  const totals = useMemo(() => countStructure(skeleton || []), [skeleton]);

  const term = search.trim().toLowerCase();
  const individualActive = Boolean(individual && individualRefs?.status === 'loaded');
  // Only search and the Individual filter auto-expand the structure — their
  // matches would otherwise sit invisible inside collapsed branches. The
  // cluster / BU / Unit Type dropdowns just narrow the tree and leave expansion
  // exactly as the user had it.
  const autoExpand = Boolean(term) || individualActive;

  // The cluster dropdown selects a subtree; Employee.clusterId is one exact id.
  const selectedClusterIds = useMemo(() => {
    if (!clusterId || !skeleton) return null;
    const subtree = findClusterSubtree(skeleton, clusterId);
    return subtree ? collectClusterIds(subtree) : new Set();
  }, [clusterId, skeleton]);

  // ADR-104 §5: a filter that selects on a property this population does not
  // have REMOVES the node — it does not show it empty. "0 unallocated in this
  // BU" is a sentence with no truth value; the individuals have no BU at all.
  const showUnallocated = Boolean(unallocated)
    && !businessUnitId
    && !orgUnitTypeCode
    && !projectPurposeId
    && (!term || 'unallocated'.includes(term))
    && (!individualActive || Boolean(individualRefs.unallocated));

  const filteredForest = useMemo(() => {
    if (!skeleton) return [];
    return applyFilters(skeleton, {
      clusterId,
      businessUnitId,
      orgUnitTypeCode,
      term,
      individualProjectIds: individualActive ? individualRefs.projectIds : null,
      projectPurposeId,
    });
  }, [skeleton, clusterId, businessUnitId, orgUnitTypeCode, term, individualActive, individualRefs, projectPurposeId]);

  // Flatten the filtered forest into visible rows. While search or the
  // Individual filter is active, structural levels auto-expand so matches are
  // actually on screen — projects do NOT auto-expand for search, since
  // expanding a project triggers a fetch.
  const rows = useMemo(() => {
    const out = [];
    const isOpen = (key) => autoExpand || expanded.has(key);

    // Individuals on the team including its sub-teams — one number whose
    // meaning does not change with the team's expansion state.
    const countTeamTree = (team, allocationRows) =>
      allocationRows.filter((allocation) => allocation.teamId === team.id).length
      + team.childTeams.reduce((sum, child) => sum + countTeamTree(child, allocationRows), 0);

    // Sub-teams are collapsed by default: only an explicit toggle opens them —
    // except under the Individual filter, where the teams on the person's
    // path open (and empty teams disappear) so their rows are actually visible.
    const visitTeam = (team, depth, allocationRows) => {
      if (individualActive && countTeamTree(team, allocationRows) === 0) return;
      const teamAllocations = allocationRows.filter(
        (allocation) => allocation.teamId === team.id
      );
      const key = `team:${team.id}`;
      const open = expanded.has(key) || individualActive;
      out.push({
        key,
        kind: 'team',
        depth,
        node: team,
        count: countTeamTree(team, allocationRows),
        expandable: teamAllocations.length > 0 || team.childTeams.length > 0,
        expanded: open,
      });
      if (!open) return;
      teamAllocations.forEach((allocation) => {
        out.push({ key: `alloc:${allocation.id}`, kind: 'allocation', depth: depth + 1, node: allocation });
      });
      team.childTeams.forEach((child) => visitTeam(child, depth + 1, allocationRows));
    };

    const visitProject = (project, depth) => {
      const key = `project:${project.id}`;
      // Under the Individual filter every surviving project IS one of the
      // person's projects — open it so their rows are on screen.
      const open = expanded.has(key) || individualActive;
      out.push({ key, kind: 'project', depth, node: project, expandable: true, expanded: open });
      if (!open) return;

      const allocationState = allocationsByProject[project.id];
      if (!allocationState || allocationState.status === 'loading') {
        out.push({ key: `${key}:loading`, kind: 'note', depth: depth + 1, note: 'loading' });
        return;
      }
      if (allocationState.status === 'error') {
        out.push({ key: `${key}:error`, kind: 'note', depth: depth + 1, note: 'error', projectId: project.id });
        return;
      }

      // Under the Individual filter, only that person's rows render — the
      // question being answered is "where are they", not "who else is there".
      const allocationRows = individualActive
        ? allocationState.rows.filter((allocation) => allocation.employee.id === individual.id)
        : allocationState.rows;
      const knownTeamIds = collectTeamIds(project.teams);
      // Root teams mirror the project 1:1 (same name, same manager), so their
      // rows are not rendered: their allocations sit directly under the
      // project and their sub-teams are promoted one level up.
      const rootTeamIds = new Set(project.teams.map((team) => team.id));
      // Directly under the project: team-less allocations, root-team
      // allocations (hoisted), and any allocation whose team is missing from
      // the skeleton (stale reference) — hiding those would silently drop
      // individuals.
      allocationRows
        .filter((allocation) => !allocation.teamId
          || rootTeamIds.has(allocation.teamId)
          || !knownTeamIds.has(allocation.teamId))
        .forEach((allocation) => {
          out.push({ key: `alloc:${allocation.id}`, kind: 'allocation', depth: depth + 1, node: allocation });
        });
      project.teams.forEach((rootTeam) => {
        rootTeam.childTeams.forEach((team) => visitTeam(team, depth + 1, allocationRows));
      });

      if (allocationRows.length === 0
        && project.teams.every((team) => team.childTeams.length === 0)) {
        out.push({ key: `${key}:empty`, kind: 'note', depth: depth + 1, note: 'empty' });
      }
    };

    const visitBu = (bu, depth) => {
      const key = `bu:${bu.id}`;
      const open = isOpen(key);
      const expandable = bu.childBus.length > 0 || bu.projects.length > 0;
      out.push({ key, kind: 'businessUnit', depth, node: bu, expandable, expanded: open });
      if (!open) return;
      bu.childBus.forEach((child) => visitBu(child, depth + 1));
      bu.projects.forEach((project) => visitProject(project, depth + 1));
    };

    const visitCluster = (cluster, depth) => {
      const key = `cluster:${cluster.id}`;
      const open = isOpen(key);
      const expandable = cluster.childClusters.length > 0 || cluster.businessUnits.length > 0;
      out.push({ key, kind: 'cluster', depth, node: cluster, expandable, expanded: open });
      if (!open) return;
      cluster.childClusters.forEach((child) => visitCluster(child, depth + 1));
      // Root BUs mirror the cluster 1:1 (the cluster manager IS the root BU's owner —
      // manager history writes one onto the other), so their rows are not
      // rendered: their child BUs and projects hoist under the cluster — the same
      // principle as Project > root team. An BU sitting at the top of the
      // filtered tree because of the BU dropdown (parentBuId set) is NOT a
      // real root and keeps its row.
      const realRootSus = cluster.businessUnits.filter((bu) => !bu.parentBuId);
      const nonRootSus = cluster.businessUnits.filter((bu) => bu.parentBuId);
      const hoistedSus = [...realRootSus.flatMap((rootSu) => rootSu.childBus), ...nonRootSus];
      const hoistedProjects = realRootSus.flatMap((rootSu) => rootSu.projects);
      hoistedSus.forEach((bu) => visitBu(bu, depth + 1));
      hoistedProjects.forEach((project) => visitProject(project, depth + 1));
    };

    filteredForest.forEach((cluster) => visitCluster(cluster, 0));

    // ADR-104: last, after the final cluster, and outside the forest entirely.
    if (showUnallocated) {
      const state = unallocatedRows;
      const open = expanded.has(UNALLOCATED_KEY)
        || (individualActive && individualRefs.unallocated);

      const visibleRows = state?.status === 'loaded'
        ? state.rows.filter((person) => (
          (!selectedClusterIds || selectedClusterIds.has(person.clusterId))
          && (!individualActive || person.id === individual.id)
        ))
        : null;

      // Collapsed, the count comes from the summary; open, from the rows on
      // screen — so the number always means what is (or would be) below it.
      const collapsedCount = selectedClusterIds
        ? unallocated.byCluster
          .filter((group) => selectedClusterIds.has(group.clusterId))
          .reduce((sum, group) => sum + group.count, 0)
        : unallocated.total;

      out.push({
        key: UNALLOCATED_KEY,
        kind: 'unallocatedRoot',
        depth: 0,
        count: visibleRows ? visibleRows.length : collapsedCount,
        expandable: true,
        expanded: open,
      });

      if (open) {
        if (!state || state.status === 'loading') {
          out.push({ key: `${UNALLOCATED_KEY}:loading`, kind: 'note', depth: 1, note: 'unallocatedLoading' });
        } else if (state.status === 'error') {
          out.push({ key: `${UNALLOCATED_KEY}:error`, kind: 'note', depth: 1, note: 'unallocatedError' });
        } else if (visibleRows.length === 0) {
          out.push({ key: `${UNALLOCATED_KEY}:empty`, kind: 'note', depth: 1, note: 'unallocatedEmpty' });
        } else {
          const nameByCluster = new Map(
            unallocated.byCluster.map((group) => [group.clusterId, group.clusterName])
          );
          const groups = new Map();
          visibleRows.forEach((person) => {
            const groupKey = person.clusterId || null;
            if (!groups.has(groupKey)) groups.set(groupKey, []);
            groups.get(groupKey).push(person);
          });
          const ordered = [...groups.entries()].sort((a, b) => {
            if (a[0] === null) return 1; // "No Cluster" always last
            if (b[0] === null) return -1;
            return (nameByCluster.get(a[0]) || '').localeCompare(nameByCluster.get(b[0]) || '');
          });
          // With one group the header would only repeat what the cluster filter (or
          // the company's shape) already says — skip it and show the people.
          const skipHeaders = ordered.length === 1;

          ordered.forEach(([groupClusterId, people]) => {
            const groupKey = `${UNALLOCATED_KEY}:group:${groupClusterId || 'none'}`;
            // Only the Individual filter opens a group without being asked —
            // the same exception the tree already makes for projects and
            // sub-teams, so the match is actually on screen.
            const groupOpen = skipHeaders || individualActive || expanded.has(groupKey);
            if (!skipHeaders) {
              out.push({
                key: groupKey,
                kind: 'unallocatedGroup',
                depth: 1,
                name: groupClusterId
                  ? (nameByCluster.get(groupClusterId) || 'Cluster')
                  : 'No Cluster',
                hasCluster: Boolean(groupClusterId),
                count: people.length,
                expandable: true,
                expanded: groupOpen,
              });
            }
            if (!groupOpen) return;
            people.forEach((person) => {
              out.push({
                key: `unalloc:${person.id}`,
                kind: 'individual',
                depth: skipHeaders ? 1 : 2,
                node: person,
              });
            });
          });
        }
      }
    }

    return out;
  }, [filteredForest, expanded, autoExpand, allocationsByProject, individualActive, individual,
    showUnallocated, unallocated, unallocatedRows, selectedClusterIds, individualRefs]);

  const renderPerson = (person, role, liveFallback = false) => (
    <PersonCell key={`${role}:${person.id}`}>
      <span className="role">{role}</span>
      <EmployeeAvatar employee={person} size={22} />
      {liveFallback ? (
        // ADR-103: no manager_history row covers the date — the current live
        // value is shown as an approximation, and says so.
        <Tooltip title="No history covers this date — current value shown as an approximation">
          <span className="name">{person.fullName} *</span>
        </Tooltip>
      ) : (
        <span className="name">{person.fullName}</span>
      )}
    </PersonCell>
  );

  const renderRow = (row) => {
    const { key, kind, depth, node } = row;

    if (kind === 'note') {
      return (
        <TreeRow key={key} $depth={depth}>
          <ExpandSpacer />
          {row.note === 'loading' && (
            <InlineNote><CircularProgress size={14} /> Loading allocations…</InlineNote>
          )}
          {row.note === 'error' && (
            <InlineNote $error>
              Failed to load allocations.
              <button type="button" className="retry" onClick={() => loadAllocations(row.projectId)}>
                Retry
              </button>
            </InlineNote>
          )}
          {row.note === 'empty' && (
            <InlineNote>
              {asOfActive
                ? 'No one was allocated to this project on this date.'
                : 'No one is currently allocated to this project.'}
            </InlineNote>
          )}
          {row.note === 'unallocatedLoading' && (
            <InlineNote><CircularProgress size={14} /> Loading unallocated individuals…</InlineNote>
          )}
          {row.note === 'unallocatedError' && (
            <InlineNote $error>
              Failed to load unallocated individuals.
              <button type="button" className="retry" onClick={loadUnallocated}>
                Retry
              </button>
            </InlineNote>
          )}
          {row.note === 'unallocatedEmpty' && (
            <InlineNote>Everyone here holds at least one active project allocation.</InlineNote>
          )}
        </TreeRow>
      );
    }

    if (kind === 'allocation') {
      const { employee, engagementType, status } = node;
      // House order everywhere else in the app: Position · Grade
      const meta = [employee.position?.label, employee.grade?.label].filter(Boolean).join(' · ');
      return (
        <TreeRow key={key} $depth={depth}>
          <ExpandSpacer />
          <AllocationInfo>
            <EmployeeAvatar employee={employee} size={24} />
            <span className="name">{employee.fullName}</span>
            {meta && <span className="meta">{meta}</span>}
          </AllocationInfo>
          <RowAside>
            {engagementType && (
              <EngagementChip>{engagementType.name || engagementType.code}</EngagementChip>
            )}
            <StatusChip $status={status}>{status}</StatusChip>
          </RowAside>
        </TreeRow>
      );
    }

    // ADR-104 — the Unallocated node, its cluster groups, and their individuals.
    if (kind === 'unallocatedRoot' || kind === 'unallocatedGroup') {
      const isRoot = kind === 'unallocatedRoot';
      const label = isRoot ? 'Unallocated' : row.name;
      const tip = isRoot
        ? 'Individuals with no active project allocation — on no project at all, bench included (a bench seat is an allocation, so bench individuals appear in the tree above). Grouped by the Cluster on their profile.'
        : (row.hasCluster
          ? (asOfActive
            // ADR-104 Amendment 1: clusterId is an unversioned attribute, so
            // at a past date this grouping is the CURRENT one, marked as such
            // rather than dropped — the ADR-103 live-fallback convention.
            ? 'Grouped by the Cluster currently recorded on the individual — this attribute is not versioned, so it is not their Cluster as of the selected date'
            : 'Grouped by the Cluster recorded on the individual — an attribute, not a position in the structure above')
          : 'These individuals have no Cluster recorded on their profile');
      return (
        <TreeRow key={key} $depth={depth}>
          <ExpandButton
            onClick={isRoot ? toggleUnallocated : () => toggleNode(key)}
            aria-label={row.expanded ? 'Collapse' : 'Expand'}
          >
            {row.expanded ? <FaChevronDown /> : <FaChevronRight />}
          </ExpandButton>
          {/* Deliberately NOT a TypeTag: that palette means "level of the
              structure", and this node is not one. The cluster groups get no tag at
              all — a cluster tag here would claim membership the data cannot back. */}
          {isRoot ? <MutedTag>—</MutedTag> : <ExpandSpacer />}
          <Tooltip title={tip}>
            <NodeName $bold={isRoot}>
              {label}{!isRoot && asOfActive && row.hasCluster ? ' *' : ''}
            </NodeName>
          </Tooltip>
          <RowAside>
            <CountTag>
              {row.count === null || row.count === undefined
                ? '— individuals'
                : `${row.count} ${row.count === 1 ? 'individual' : 'individuals'}`}
            </CountTag>
          </RowAside>
        </TreeRow>
      );
    }

    if (kind === 'individual') {
      // House order everywhere else in the app: Position · Grade
      const meta = [node.position?.label, node.grade?.label].filter(Boolean).join(' · ');
      return (
        <TreeRow key={key} $depth={depth}>
          <ExpandSpacer />
          <AllocationInfo>
            <EmployeeAvatar employee={node} size={24} />
            <span className="name">{node.fullName}</span>
            {meta && <span className="meta">{meta}</span>}
          </AllocationInfo>
          <RowAside>
            {/* The difference between a staffing question and a data question */}
            {node.lastAllocationEndDate ? (
              <Tooltip title="Last day of their most recent project allocation">
                <CountTag>off project since {formatDateOnly(node.lastAllocationEndDate, 'DD/MM/YYYY')}</CountTag>
              </Tooltip>
            ) : (
              <Tooltip title="No project allocation has ever been recorded for this individual">
                <CountTag>never allocated</CountTag>
              </Tooltip>
            )}
            {/* Employee status (ADR-017), not allocation status — they have no allocation */}
            <StatusChip $status={node.status}>{node.status}</StatusChip>
          </RowAside>
        </TreeRow>
      );
    }

    const expandable = kind === 'project' ? true : row.expandable;
    const onToggle = kind === 'project'
      ? () => toggleProject(node)
      : () => toggleNode(key);

    return (
      <TreeRow key={key} $depth={depth}>
        {expandable ? (
          <ExpandButton onClick={onToggle} aria-label={row.expanded ? 'Collapse' : 'Expand'}>
            {row.expanded ? <FaChevronDown /> : <FaChevronRight />}
          </ExpandButton>
        ) : (
          <ExpandSpacer />
        )}
        <TypeTag $color={TYPE_COLORS[kind]}>{TYPE_LABELS[kind]}</TypeTag>
        <NodeName
          $bold={kind === 'cluster' || kind === 'businessUnit'}
          $muted={kind === 'businessUnit' && node.status === 'Inactive'}
        >
          {node.name}
        </NodeName>
        {kind === 'project' && node.code && <NodeDetail>{node.code}</NodeDetail>}
        {kind === 'cluster' && node.orgStructureType?.code && (
          <Tooltip title={`Organizational Structure Type: ${node.orgStructureType.name}`}>
            <NodeDetail>{node.orgStructureType.code}</NodeDetail>
          </Tooltip>
        )}
        {kind === 'project' && node.purpose && (
          <Tooltip title={node.purpose.description || 'Project purpose'}>
            <CountTag>{node.purpose.name}</CountTag>
          </Tooltip>
        )}
        {kind === 'businessUnit' && node.orgUnitType && (
          <NodeDetail>{node.orgUnitType.name}</NodeDetail>
        )}
        {/* ADR-120: the server derives status against the as-of date (or today) */}
        {kind === 'businessUnit' && node.status && node.status !== 'Active' && (
          <Tooltip title={node.endReason ? `Reason: ${node.endReason}` : ''} disableHoverListener={!node.endReason}>
            <LifecycleTag $ending={node.status === 'Ending'}>
              {node.status === 'Ending' ? `Ends ${formatDateOnly(node.endDate)}` : `Ended ${formatDateOnly(node.endDate)}`}
            </LifecycleTag>
          </Tooltip>
        )}
        <RowAside>
          {kind === 'cluster' && node.managers.map((manager) => renderPerson(manager, 'Cluster Manager', node.managerLiveFallback))}
          {kind === 'businessUnit' && node.owner && renderPerson(node.owner, 'Owner', node.ownerLiveFallback)}
          {(kind === 'project' || kind === 'team') && node.manager
            && renderPerson(node.manager, 'Manager', node.managerLiveFallback)}
          {kind === 'businessUnit' && node.projects.length > 0 && (
            <CountTag>
              {node.projects.length} {node.projects.length === 1 ? 'project' : 'projects'}
            </CountTag>
          )}
          {kind === 'businessUnit' && node.projects.length > 0 && (
            <Tooltip title="Distinct individuals across this BU's own projects — an individual on several of them counts once, so this can be less than the project tags added up">
              <CountTag>
                {node.employeeCount} {node.employeeCount === 1 ? 'individual' : 'individuals'}
              </CountTag>
            </Tooltip>
          )}
          {kind === 'project' && (
            <Tooltip title="Distinct individuals with a current or upcoming allocation, any engagement type">
              <CountTag>
                {node.employeeCount} {node.employeeCount === 1 ? 'individual' : 'individuals'}
              </CountTag>
            </Tooltip>
          )}
          {kind === 'team' && (
            <Tooltip title="Individuals on this team, including its sub-teams">
              <CountTag>
                {row.count} {row.count === 1 ? 'individual' : 'individuals'}
              </CountTag>
            </Tooltip>
          )}
        </RowAside>
      </TreeRow>
    );
  };

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/reports')}>
            <FaArrowLeft /> Back to Reports
          </BackButton>
          <Title>Organization Hierarchy</Title>
        </HeaderLeft>
        <HeaderStats>
          {isLoading ? (
            <CircularProgress size={20} />
          ) : (
            <>
              <HeaderStat>
                <div className="top">
                  <span className="label">Clusters</span>
                  <span className="value">{totals.clusters}</span>
                </div>
              </HeaderStat>
              <HeaderStat>
                <div className="top">
                  <span className="label">Business Units</span>
                  <span className="value">{totals.businessUnits}</span>
                </div>
              </HeaderStat>
              <HeaderStat>
                <div className="top">
                  <span className="label">Projects</span>
                  <span className="value">{totals.projects}</span>
                </div>
              </HeaderStat>
              {/* ADR-104: the one headcount in an otherwise structural header —
                  the number this report gets asked for, already in hand. */}
              <HeaderStat>
                <div className="top">
                  <span className="label">Unallocated</span>
                  {/* The unallocated population does not depend on org_edge_history,
                      so it IS answerable pre-backfill — but the report refuses that
                      date, and one number answering where the tree will not would
                      be worse than no number. */}
                  <span className="value">{preHistory ? '—' : (unallocated?.total ?? '—')}</span>
                </div>
              </HeaderStat>
            </>
          )}
        </HeaderStats>
      </Header>

      <Content>
        <MainSection>
          <FiltersBar>
            <ClusterSelector
              value={clusterId}
              onChange={setClusterId}
              label="Cluster"
              size="small"
              fullWidth={false}
              sx={{ minWidth: 180 }}
              showStructureType // ADR-119
            />
            <BusinessUnitSelector
              value={businessUnitId}
              onChange={setBusinessUnitId}
              label="Business Unit"
              showStructureType // ADR-119
              size="small"
              fullWidth={false}
              sx={{ minWidth: 180 }}
            />
            <OrgUnitTypeSelector
              value={orgUnitTypeCode}
              onChange={setOrgUnitTypeCode}
              returnValue={ORG_UNIT_TYPE_RETURN_TYPES.CODE}
              emptyOptionLabel="All Types"
              size="small"
              fullWidth={false}
              sx={{ minWidth: 210 }}
            />
            {/* Narrows to BUs/clusters that contain projects of this purpose */}
            <ProjectPurposeSelector
              value={projectPurposeId}
              onChange={setProjectPurposeId}
              label="Project Purpose"
              emptyOptionLabel="All Purposes"
              size="small"
              fullWidth={false}
              sx={{ minWidth: 190 }}
            />
            {/* Prunes the tree to where this person is allocated and opens
                their projects, showing only their rows. ADR-103: unavailable
                in as-of mode until its endpoint learns asOfDate (phase 2). */}
            {asOfActive ? (
              <Tooltip title="The Individual filter is not available when viewing a past date yet">
                <TextField
                  value=""
                  label="Individual"
                  placeholder="Not available as of a date"
                  size="small"
                  disabled
                  sx={{ minWidth: 240 }}
                />
              </Tooltip>
            ) : (
              <EmployeeSearchProvider instanceId="org-hierarchy-individual">
                <EmployeeSearch
                  value={individual}
                  onChange={setIndividual}
                  label="Individual"
                  placeholder="Search individuals..."
                  width={240}
                  height={40}
                  hideLabel
                />
              </EmployeeSearchProvider>
            )}
            <TextField
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              label="Search"
              placeholder="Name, code, owner…"
              size="small"
              sx={{ minWidth: 240 }}
            />
            {/* ADR-103: view the structure as it was on a past date */}
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
              <DatePicker
                label="As of date"
                value={asOfDate}
                onChange={(newValue) => {
                  setAsOfDate(newValue);
                  // The Individual filter is unavailable in as-of mode (phase 2)
                  if (newValue) setIndividual(null);
                }}
                disableFuture
                minDate={toPickerValue(historyStartsAt) ?? undefined}
                slotProps={{
                  textField: { size: 'small', sx: { minWidth: 170 } },
                  field: { clearable: true },
                }}
              />
            </LocalizationProvider>
          </FiltersBar>

          {asOfActive && !isLoading && !preHistory && (
            <Alert severity="info" sx={{ mb: 1 }}>
              Showing the organization as it was on {asOfDate.format('DD/MM/YYYY')}.
              Names and attributes are current; owners marked with * are live
              approximations where history has no coverage. Unallocated
              individuals are resolved at this date, but their Cluster
              grouping (marked *) is the current one — it is not versioned; and
              a re-hired individual is resolved from their current employment
              only, so a date inside an earlier stint may omit them.
            </Alert>
          )}

          {isLoading ? (
            <LoadingSpinner message="Loading organization structure..." />
          ) : preHistory ? (
            <EmptyState
              title="Structure history does not reach this date"
              description={historyStartsAt
                ? `Structure history starts on ${formatDateOnly(historyStartsAt)} — pick that date or a later one.`
                : 'Structure history does not cover the selected date — pick a more recent one.'}
            />
          ) : loadFailed ? (
            <EmptyState
              title="Could not load the organization hierarchy"
              description="Something went wrong while loading the structure. Refresh the page to try again."
            />
          ) : rows.length === 0 ? (
            <EmptyState
              title="Nothing matches"
              description={individualActive
                ? (individualRefs.unallocated
                  // ADR-104: they DO have a place now — it is just filtered out
                  ? `${individual.fullName} has no project allocation at all, so they appear under Unallocated — which the current filters hide. Clear the Business Unit, Unit Type or Purpose filter to see them.`
                  : `${individual.fullName} has no current or upcoming project allocations matching the other filters.`)
                : 'No clusters, business units or projects match the current filters.'}
            />
          ) : (
            rows.map(renderRow)
          )}
        </MainSection>
      </Content>
    </Container>
  );
};

export default OrganizationHierarchy;
